import { defaultApi } from "@/api";
import i18n from "@/i18n";
import { useUserStore } from "@/store";
import { useNotificationDrawerStore } from "@/store";
import { DateTime } from "luxon";
import { ref, Ref } from "vue";
import { DEFAULT_FILE_TYPE } from "@/global/data.ts";
import { AxiosResponse } from "axios";
import { logInfo, logError } from "@/loggers";
import { FEATURE_NAMES } from "@/loggers/types";

const excelLoading: Ref<boolean> = ref(false);
const exportExcelErrorMessage = ref<string>(null);

export const getFormattedDate = (inputDate: string, format: string, showTimezoneName: boolean) => {
  const dateTime = DateTime.fromISO(inputDate, { zone: "utc" });
  const formattedDate = dateTime.toFormat(format);
  if (showTimezoneName) {
    const timeZoneName = DateTime.fromISO(inputDate, { zone: "utc" }).offsetNameShort;
    return `${formattedDate} (${timeZoneName})`;
  } else {
    return formattedDate;
  }
};
const exportAsyncExcel = async (
  fileName: string,
  productId: string,
  searchParams?: Record<string, any>,
  requestBody?: Record<string, any>
) => {
  const notificationDrawerStore = useNotificationDrawerStore();
  notificationDrawerStore.setFileName(fileName);
  const payload = {
    productId,
    fileName,
    searchParams,
    ...(requestBody ? { requestBody } : {})
  };
  try {
    await defaultApi.apiUnversioned.downloadLists.post(payload);
    logInfo(FEATURE_NAMES.MVS_RISK_MANAGEMENT, "Download initiated for:" + productId + " and fileName is:" + fileName);
    excelLoading.value = false;
  } catch (err) {
    logError(FEATURE_NAMES.MVS_RISK_MANAGEMENT, "Error in intitating Download" + err);
    exportExcelErrorMessage.value = i18n.global.t("SOME_ERROR_OCCURED");
    excelLoading.value = false;
  } finally {
    excelLoading.value = false;
  }
};
const exportAsyncExcelLogic = {
  exportExcelErrorMessage,
  excelLoading,
  exportAsyncExcel
};

const AUTO_DOWNLOADED_JOB_IDS_KEY = "mvs:autoDownloadedJobIds";
const AUTO_DOWNLOADED_JOB_IDS_LIMIT = 200;

const readAutoDownloadedJobIds = (): string[] => {
  try {
    const raw = localStorage.getItem(AUTO_DOWNLOADED_JOB_IDS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
};

export const hasJobBeenAutoDownloaded = (jobId?: string): boolean => {
  if (!jobId) return false;
  return readAutoDownloadedJobIds().includes(jobId);
};

export const markJobAsAutoDownloaded = (jobId?: string): void => {
  if (!jobId) return;
  try {
    const ids = readAutoDownloadedJobIds();
    if (ids.includes(jobId)) return;
    ids.push(jobId);
    const trimmed =
      ids.length > AUTO_DOWNLOADED_JOB_IDS_LIMIT ? ids.slice(ids.length - AUTO_DOWNLOADED_JOB_IDS_LIMIT) : ids;
    localStorage.setItem(AUTO_DOWNLOADED_JOB_IDS_KEY, JSON.stringify(trimmed));
  } catch (err) {
    logError(FEATURE_NAMES.MVS_RISK_MANAGEMENT, "Failed to persist auto-downloaded job ids to localStorage: " + err);
  }
};

export const asyncDownloadFileInBrowser = (
  fileName: string,
  fileType: string = DEFAULT_FILE_TYPE,
  response?: AxiosResponse,
  downloadUrl?: any
) => {
  try {
    const url = downloadUrl || (response && URL.createObjectURL(new Blob([response.data], { type: fileType })));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    logInfo(
      FEATURE_NAMES.MVS_RISK_MANAGEMENT,
      "Downloading file in browser: " +
        fileName +
        " with type: " +
        fileType +
        (downloadUrl ? " from URL: " + downloadUrl : " from response data")
    );
  } catch (err) {
    logError(FEATURE_NAMES.MVS_RISK_MANAGEMENT, "Error in downloading file in browser" + err);
  }
};
const getIsCustomerFeatureFlagEnabled = (flag: string) => {
  const { isCustomerFeatureFlagEnabled } = useUserStore();
  return isCustomerFeatureFlagEnabled(flag);
};

export const isAsyncReportingEnabled = (): boolean => getIsCustomerFeatureFlagEnabled("vite_async_reporting");

export default exportAsyncExcelLogic;
